<template>
    <v-select
        ref="selectItemPo"
        :placeholder="placeholder"
        :options="options"
        :filterable="false"
        @search="fetchItems"
        @input="submit"
        :noDrop="isDisableDrop"
        :disabled="isDisabled"
        @close="onClose"
        class="show-actions"
        v-bind="$attrs"
        v-on="$listeners"
        :value="value"
        :searchable="true"
        @search:blur="setSearchText"
    >
        <template v-slot:no-options="{ search }">
            <div class="no-result">
                <span v-if="search === ''">
                    Type to search
                </span>
                <span v-else-if="isLoading">
                    Loading...
                </span>
                <span v-else>
                    {{ emptyOptionsMessage }}
                </span>
            </div>
        </template>

        <template v-slot:open-indicator>
            <!-- this empty div is intentional, do not remove. this is for a "replacement" to default arrow -->
            <div></div>
        </template>

        <template v-slot:spinner>
            <img
                v-if="isLoading"
                class="spinner"
                src="/apps/assets/img/ajax-spinner.gif"
            />
        </template>

        <template v-slot:list-footer>
            <li ref="footerLoader" class="footerloader" v-if="hasNextPage">
                Loading...
            </li>
        </template>
    </v-select>
</template>

<script>
import vSelect from "vue-select";
import { debounce } from "lodash";
import ItemServices from "@/services/items";

export default {
    components: {
        vSelect
    },
    props: {
        placeholder: {
            type: String
        },
        emptyOptionsMessage: {
            type: String,
            default: "No matches found"
        },
        isDisabled: {
            type: Boolean,
            default: false
        },
        focusOnload: {
            default: false,
            type: Boolean
        },
        /**
         * source catalog is used for purchase order item listing
         * based on supplier code
         * (module: item-po)
         */
        sourceCatalog: {
            type: [String, null],
            default: null
        },
        value: [Object, String, Array, Number]
    },
    data() {
        return {
            query: "",
            isDisableDrop: true,
            options: [],
            isLoading: false,
            resultMeta: null,
            currentPatient: "",
            search_text: "",
            hasNextPage: false
        };
    },
    mounted() {
        this.observer = new IntersectionObserver(this.infiniteScroll);

        if (this.focusOnload) {
            this.focus();
        }
    },
    methods: {
        focus() {
            this.$refs.selectItemPo.$el.querySelector("input").focus();
        },
        submit(value) {
            this.$emit("select", value);
            this.query = "";
            this.options = [];
        },
        fetchItems(search) {
            this.query = search;
            this.search_text = search;
            this.isLoading = search !== "" ? true : false;
            this.searchItems(search, this);
        },
        searchItems: debounce(async (search, self) => {
            if (search === "") {
                return;
            }

            let params = {
                search_key: search,
                source: self.sourceCatalog,
                perPage: 30,
                page: 1
            };

            ItemServices.getIntergratedItemsWithSearch(params)
                .then(res => {
                    if (res && res.code == 200 && res.resources && res.meta) {
                        self.processResults(res.resources, res.meta);
                    } else {
                        self.options = [];
                        self._message("Error : Unable to get items", "danger");
                    }
                })
                .then(() => {
                    self.registerFootLoader();
                })
                .catch(error => {
                    self._message("Error : Unable to get items", "danger");
                    console.error(error);
                })
                .finally(() => {
                    self.isLoading = false;
                });
        }, 350),
        processResults(resources, meta, isNextPage = false) {
            let self = this;

            if (isNextPage) {
                let newOptions = resources;

                newOptions = newOptions.map(item => ({
                    ...item,
                    label: item.display_name
                }));

                self.options = [...self.options, ...newOptions];
            } else {
                self.options = resources;

                // append "label" key
                self.options = self.options.map(item => ({
                    ...item,
                    label: item.display_name
                }));
            }

            self.isDisableDrop = false;
            self.isLoading = false;

            self.resultMeta = meta;

            if (self.resultMeta.currentPage != self.resultMeta.lastPage) {
                self.hasNextPage = true;
            } else {
                self.hasNextPage = false;
            }
        },
        async registerFootLoader() {
            if (this.hasNextPage) {
                await this.$nextTick();
                this.observer.observe(this.$refs.footerLoader);
            }
        },
        onClose() {
            this.observer.disconnect();
            this.options = []; // clear
            this.hasNextPage = false;
            this.resultMeta = null;
        },
        async infiniteScroll([{ isIntersecting, target }]) {
            const self = this;

            // references: https://vue-select.org/guide/infinite-scroll.html
            if (isIntersecting && this.hasNextPage) {
                const ul = target.offsetParent;
                const scrollTop = target.offsetParent.scrollTop;
                await this.$nextTick();

                this.fetchNextPage(async () => {
                    await self.$nextTick();
                    ul.scrollTop = scrollTop; // keep scroll position after we append the options
                });
            }
        },
        async fetchNextPage(callback) {
            if (!this.resultMeta) {
                return;
            }
            let self = this;
            const { currentPage, lastPage } = self.resultMeta;
            let nextPage = currentPage + 1;

            if (currentPage >= lastPage) {
                self.hasNextPage = false;
                return;
            }

            let params = {
                search_key: self.query,
                source: self.sourceCatalog,
                perPage: 30,
                page: nextPage
            };

            ItemServices.getIntergratedItemsWithSearch(params)
                .then(res => {
                    if (res && res.code == 200 && res.resources && res.meta) {
                        self.processResults(res.resources, res.meta, true);
                    } else {
                        self.options = [];
                        self._message("Error : Unable to get items", "danger");
                    }
                })
                .catch(error => {
                    self.options = [];
                    self._message("Error : Unable to get items", "danger");
                    console.error(error);
                })
                .finally(() => {
                    if (callback) {
                        callback();
                    }
                });
        },
        // Defocus will lost the current text, save it to somewhere else.
        setSearchText(event) {
            this.txt = this.search_text;
            this.$emit("search:blur", event);
        }
    }
};
</script>

<style scoped>
.no-result {
    margin-left: 10px;
}
.footerloader {
    padding: 3px 20px;
    font-style: italic;
}
</style>
